import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import puzzleConfig from '../config/puzzles.json';
import './DigitalPuzzle.css';

function DigitalPuzzle({
  onComplete,
  onProgress,
  initialPuzzle = null,
  user = null,
}) {
  // Config-driven state
  const [config] = useState(puzzleConfig);
  const [puzzles, setPuzzles] = useState([]);
  const [selectedPuzzle, setSelectedPuzzle] = useState(initialPuzzle);
  const [selectedDifficulty, setSelectedDifficulty] = useState(
    config.defaultDifficulty
  );
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Game state
  const [pieces, setPieces] = useState([]);
  const [boardPieces, setBoardPieces] = useState([]);
  const [draggedPiece, setDraggedPiece] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [loading, setLoading] = useState(true);

  // Get current difficulty settings
  const difficultySettings = config.difficulties[selectedDifficulty];
  const gridSize = difficultySettings?.gridSize || config.defaultGridSize;
  const totalPieces = gridSize * gridSize;

  // Load puzzles from config and Firestore
  useEffect(() => {
    loadPuzzles();
  }, []);

  const loadPuzzles = async () => {
    setLoading(true);
    try {
      // Start with sample puzzles from config
      let allPuzzles = [...config.samplePuzzles];

      // Load custom puzzles from Firestore if user is logged in
      if (user) {
        try {
          const customPuzzlesRef = collection(db, 'customPuzzles');
          const q = query(
            customPuzzlesRef,
            where('createdBy', '==', user.uid),
            orderBy('createdAt', 'desc')
          );
          const snapshot = await getDocs(q);

          snapshot.forEach((doc) => {
            allPuzzles.push({
              id: doc.id,
              ...doc.data(),
              category: 'custom',
              isCustom: true,
            });
          });
        } catch (err) {
          console.log('No custom puzzles or index not ready:', err.message);
        }
      }

      // Also load shared/public puzzles
      try {
        const publicPuzzlesRef = collection(db, 'publicPuzzles');
        const publicQuery = query(
          publicPuzzlesRef,
          where('isPublic', '==', true)
        );
        const publicSnapshot = await getDocs(publicQuery);

        publicSnapshot.forEach((doc) => {
          // Avoid duplicates
          if (!allPuzzles.find((p) => p.id === doc.id)) {
            allPuzzles.push({
              id: doc.id,
              ...doc.data(),
              isPublic: true,
            });
          }
        });
      } catch (err) {
        console.log('No public puzzles available:', err.message);
      }

      setPuzzles(allPuzzles);
    } catch (error) {
      console.error('Error loading puzzles:', error);
      // Fall back to config puzzles only
      setPuzzles(config.samplePuzzles);
    } finally {
      setLoading(false);
    }
  };

  // Filter puzzles by category
  const filteredPuzzles = puzzles.filter((puzzle) => {
    if (selectedCategory === 'all') return true;
    return puzzle.category === selectedCategory;
  });

  // Initialize puzzle when selected
  useEffect(() => {
    if (selectedPuzzle) {
      initializePuzzle();
    }
  }, [selectedPuzzle, selectedDifficulty]);

  // Timer
  useEffect(() => {
    let interval;
    if (startTime && !completed) {
      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [startTime, completed]);

  const initializePuzzle = () => {
    // Create pieces with their correct positions based on current grid size
    const newPieces = [];
    for (let i = 0; i < totalPieces; i++) {
      newPieces.push({
        id: i,
        correctPosition: i,
        row: Math.floor(i / gridSize),
        col: i % gridSize,
      });
    }

    // Shuffle pieces
    const shuffled = [...newPieces].sort(() => Math.random() - 0.5);
    setPieces(shuffled);
    setBoardPieces(Array(totalPieces).fill(null));
    setCompleted(false);
    setStartTime(Date.now());
    setElapsedTime(0);
    setScore(0);
    setStreak(0);
    setImageLoaded(false);
  };

  // Calculate score for placing a piece
  const calculateScore = useCallback(
    (isCorrect) => {
      const {
        basePointsPerPiece,
        timeMultiplierMax,
        timeMultiplierDecayPerSecond,
        correctPlacementBonus,
        streakBonus,
      } = config.scoring;
      const difficultyMultiplier = difficultySettings?.timeBonus || 1;

      // Time bonus decreases over time
      const timeMultiplier = Math.max(
        0.5,
        timeMultiplierMax - elapsedTime * timeMultiplierDecayPerSecond
      );

      let points = basePointsPerPiece * timeMultiplier * difficultyMultiplier;

      if (isCorrect) {
        points += correctPlacementBonus;

        // Check for streak bonuses
        const newStreak = streak + 1;
        if (streakBonus[newStreak]) {
          points += streakBonus[newStreak];
        }
        setStreak(newStreak);
      } else {
        setStreak(0);
      }

      return Math.round(points);
    },
    [config.scoring, difficultySettings, elapsedTime, streak]
  );

  // Check if puzzle is complete
  const checkCompletion = useCallback(
    (board) => {
      const placedCount = board.filter((p) => p !== null).length;
      const progress = Math.round((placedCount / totalPieces) * 100);

      if (onProgress) {
        onProgress(progress);
      }

      if (placedCount === totalPieces) {
        // Check if all pieces are in correct positions
        const isCorrect = board.every(
          (piece, index) => piece && piece.correctPosition === index
        );

        if (isCorrect) {
          setCompleted(true);
          if (onComplete) {
            onComplete({
              puzzle: selectedPuzzle,
              difficulty: selectedDifficulty,
              time: elapsedTime,
              pieces: totalPieces,
              score: score,
            });
          }
        }
      }
    },
    [
      totalPieces,
      elapsedTime,
      onComplete,
      onProgress,
      selectedPuzzle,
      selectedDifficulty,
      score,
    ]
  );

  // Drag handlers for pieces in tray
  const handleDragStart = (e, piece) => {
    setDraggedPiece(piece);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Drag handlers for pieces on board (to move them)
  const handleBoardPieceDragStart = (e, piece, fromIndex) => {
    setDraggedPiece({ ...piece, fromBoard: true, fromIndex });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();

    if (!draggedPiece) return;

    const newBoardPieces = [...boardPieces];
    const newPieces = [...pieces];

    // If dropping on an occupied slot, swap or return to tray
    if (newBoardPieces[targetIndex] !== null) {
      const existingPiece = newBoardPieces[targetIndex];

      if (draggedPiece.fromBoard) {
        // Swap pieces on board
        newBoardPieces[draggedPiece.fromIndex] = existingPiece;
      } else {
        // Return existing piece to tray
        newPieces.push(existingPiece);
      }
    } else if (draggedPiece.fromBoard) {
      // Clear the original board position
      newBoardPieces[draggedPiece.fromIndex] = null;
    }

    // Place dragged piece
    const placedPiece = {
      id: draggedPiece.id,
      correctPosition: draggedPiece.correctPosition,
      row: draggedPiece.row,
      col: draggedPiece.col,
    };
    newBoardPieces[targetIndex] = placedPiece;

    // Calculate and add score
    const isCorrectPosition = placedPiece.correctPosition === targetIndex;
    const pointsEarned = calculateScore(isCorrectPosition);
    setScore((prev) => prev + pointsEarned);

    // Remove from tray if it came from there
    if (!draggedPiece.fromBoard) {
      const pieceIndex = newPieces.findIndex((p) => p.id === draggedPiece.id);
      if (pieceIndex > -1) {
        newPieces.splice(pieceIndex, 1);
      }
    }

    setPieces(newPieces);
    setBoardPieces(newBoardPieces);
    setDraggedPiece(null);

    // Check completion
    checkCompletion(newBoardPieces);
  };

  // Return piece from board to tray
  const handleReturnToTray = (piece, index) => {
    const newBoardPieces = [...boardPieces];
    newBoardPieces[index] = null;
    setBoardPieces(newBoardPieces);
    setPieces([...pieces, piece]);
    setStreak(0); // Reset streak when removing a piece
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const placedCount = boardPieces.filter((p) => p !== null).length;
  const progress = Math.round((placedCount / totalPieces) * 100);

  // Puzzle selection screen
  if (!selectedPuzzle) {
    return (
      <div className="digital-puzzle-selector">
        <h2>🧩 {config.messages.choosePuzzle}</h2>
        <p>{config.messages.selectPuzzle}</p>

        {/* Difficulty Selector */}
        <div className="difficulty-selector">
          <label>Difficulty:</label>
          <div className="difficulty-options">
            {Object.entries(config.difficulties).map(([key, diff]) => (
              <button
                key={key}
                className={`difficulty-btn ${
                  selectedDifficulty === key ? 'active' : ''
                }`}
                onClick={() => setSelectedDifficulty(key)}
              >
                {diff.label}
              </button>
            ))}
          </div>
        </div>

        {/* Category Filter */}
        <div className="category-filter">
          {config.categories.map((cat) => (
            <button
              key={cat.id}
              className={`category-btn ${
                selectedCategory === cat.id ? 'active' : ''
              }`}
              onClick={() => setSelectedCategory(cat.id)}
            >
              {cat.emoji} {cat.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="puzzle-loading">
            <div className="loading-spinner">🧩</div>
            <p>{config.messages.loading}</p>
          </div>
        ) : (
          <div className="puzzle-options">
            {filteredPuzzles.map((puzzle) => (
              <button
                key={puzzle.id}
                className={`puzzle-option ${puzzle.featured ? 'featured' : ''}`}
                onClick={() => setSelectedPuzzle(puzzle)}
              >
                <div
                  className="puzzle-preview"
                  style={{ backgroundImage: `url(${puzzle.imageUrl})` }}
                >
                  <span className="puzzle-emoji">{puzzle.emoji}</span>
                  {puzzle.isCustom && (
                    <span className="custom-badge">📸 My Puzzle</span>
                  )}
                  {puzzle.featured && (
                    <span className="featured-badge">⭐ Featured</span>
                  )}
                </div>
                <div className="puzzle-info">
                  <span className="puzzle-name">{puzzle.name}</span>
                  <span className="puzzle-artist">{puzzle.artist}</span>
                  <span className="puzzle-pieces">
                    {difficultySettings.pieces} pieces (
                    {difficultySettings.label})
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}

        {filteredPuzzles.length === 0 && !loading && (
          <div className="no-puzzles">
            <p>No puzzles in this category yet!</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="digital-puzzle">
      {/* Header */}
      <div className="puzzle-header">
        <button
          className="puzzle-back-btn"
          onClick={() => setSelectedPuzzle(null)}
        >
          {config.messages.back}
        </button>
        <div className="puzzle-title">
          <span className="puzzle-emoji">{selectedPuzzle.emoji}</span>
          <h2>{selectedPuzzle.name}</h2>
        </div>
        <div className="puzzle-stats">
          <span className="puzzle-timer">⏱️ {formatTime(elapsedTime)}</span>
          <span className="puzzle-score">⭐ {score}</span>
          <span className="puzzle-progress-stat">
            🧩 {placedCount}/{totalPieces}
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="puzzle-progress-bar">
        <div
          className="puzzle-progress-fill"
          style={{ width: `${progress}%` }}
        />
        <span className="puzzle-progress-text">{progress}%</span>
      </div>

      {/* Streak indicator */}
      {streak >= 3 && (
        <div className="streak-indicator">
          🔥 {streak} streak! +{config.scoring.streakBonus[streak] || 0} bonus
        </div>
      )}

      {/* Reference Image */}
      <div className="puzzle-reference">
        <img
          src={selectedPuzzle.imageUrl}
          alt={selectedPuzzle.name}
          onLoad={() => setImageLoaded(true)}
          className="reference-image"
          style={{
            maxWidth: config.ui.referenceImageMaxWidth,
            maxHeight: config.ui.referenceImageMaxHeight,
          }}
        />
        <span className="reference-label">{config.messages.reference}</span>
      </div>

      {imageLoaded && (
        <>
          {/* Puzzle Board */}
          <div
            className="puzzle-board"
            style={{
              gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
              gap: config.ui.pieceGap,
              borderRadius: config.ui.boardBorderRadius,
            }}
          >
            {boardPieces.map((piece, index) => (
              <div
                key={index}
                className={`board-slot ${piece ? 'occupied' : 'empty'} ${
                  piece && piece.correctPosition === index ? 'correct' : ''
                }`}
                style={{ borderRadius: config.ui.pieceBorderRadius }}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                onClick={() => piece && handleReturnToTray(piece, index)}
              >
                {piece && (
                  <div
                    className="board-piece"
                    draggable
                    onDragStart={(e) =>
                      handleBoardPieceDragStart(e, piece, index)
                    }
                    style={{
                      backgroundImage: `url(${selectedPuzzle.imageUrl})`,
                      backgroundSize: `${gridSize * 100}% ${gridSize * 100}%`,
                      backgroundPosition: `${
                        (piece.col / (gridSize - 1)) * 100
                      }% ${(piece.row / (gridSize - 1)) * 100}%`,
                      borderRadius: config.ui.pieceBorderRadius,
                    }}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Piece Tray */}
          <div className="puzzle-tray">
            <h3>
              {config.messages.availablePieces} ({pieces.length})
            </h3>
            <div className="tray-pieces">
              {pieces.map((piece) => (
                <div
                  key={piece.id}
                  className="tray-piece"
                  draggable
                  onDragStart={(e) => handleDragStart(e, piece)}
                  style={{
                    width: config.ui.trayPieceSize,
                    height: config.ui.trayPieceSize,
                    backgroundImage: `url(${selectedPuzzle.imageUrl})`,
                    backgroundSize: `${gridSize * 100}% ${gridSize * 100}%`,
                    backgroundPosition: `${
                      (piece.col / (gridSize - 1)) * 100
                    }% ${(piece.row / (gridSize - 1)) * 100}%`,
                    borderRadius: config.ui.pieceBorderRadius,
                  }}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {!imageLoaded && (
        <div className="puzzle-loading">
          <div className="loading-spinner">🧩</div>
          <p>{config.messages.loading}</p>
        </div>
      )}

      {/* Completion Overlay */}
      {completed && (
        <div className="puzzle-complete-overlay">
          <div className="puzzle-complete-modal">
            <div className="complete-icon">🎉</div>
            <h2>{config.messages.complete}</h2>
            <p>
              You solved "{selectedPuzzle.name}" in {formatTime(elapsedTime)}!
            </p>
            <div className="complete-stats">
              <div className="stat">
                <span className="stat-value">{totalPieces}</span>
                <span className="stat-label">Pieces</span>
              </div>
              <div className="stat">
                <span className="stat-value">{formatTime(elapsedTime)}</span>
                <span className="stat-label">Time</span>
              </div>
              <div className="stat">
                <span className="stat-value">{score}</span>
                <span className="stat-label">Score</span>
              </div>
            </div>
            <div className="complete-actions">
              <button className="play-again-btn" onClick={initializePuzzle}>
                🔄 {config.messages.playAgain}
              </button>
              <button
                className="new-puzzle-btn"
                onClick={() => setSelectedPuzzle(null)}
              >
                🧩 {config.messages.newPuzzle}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DigitalPuzzle;
